import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useMemo } from "react";

import APTable from "../../../../components/APTable";
import CRMButton from "../../../../components/buttons/CRMButton";
import { arrayToLookup } from "../../../../helpers/array";
import { toDollars } from "../../../../helpers/currencies";
import { DateTimeFormat, formatDate } from "../../../../helpers/dates";
import { getDossierTransactionCRMUrl } from "../../../../helpers/dossiers";
import {
  DossierTransaction,
  DossierTransactionStatus,
  DossierTransactionType,
  getDateForTransactionComparison,
  getTransactionStatusForTransactionComparison,
  getTransactionTypeForTransactionComparison,
  TransactionComparison,
  TransactionComparisonMismatchedField,
} from "../../../../types/DossierTransaction";

type TableDataDefinition = {
  date: string;
  transactionType: DossierTransactionType;
  transactionStatus: DossierTransactionStatus;
  comparisonStatus: "matching" | "mismatching" | "missing";
  transactionId: string;
  ceTransactionId: boolean;
  crmTransactionId: string;
  mismatchedFields: string;
};

const APInvoiceComparisonTable = ({
  transactionComparison,
  crmClientURL,
}: {
  transactionComparison: TransactionComparison[];
  crmClientURL: string | null;
}) => {
  const transactionComparisonLookup = useMemo(() => {
    return transactionComparison
      ? arrayToLookup(transactionComparison, "transactionId")
      : {};
  }, [transactionComparison ?? []]);

  const renderMismatchedFields = (
    mismatchedFields: Partial<
      Record<keyof DossierTransaction, TransactionComparisonMismatchedField>
    >
  ) => {
    return Object.entries(mismatchedFields).map(([field, value]) => {
      return (
        <Box key={field}>
          <Typography variant="bodyMedium" fontWeight={600}>
            {field}
          </Typography>
          <Typography variant="bodyMedium">CRM: {value.crmValue}</Typography>
          <Typography variant="bodyMedium">Catch-e: {value.ceValue}</Typography>
        </Box>
      );
    });
  };

  const renderMissingText = (missingCRM: boolean, missingCatchE: boolean) => {
    if (missingCRM) {
      return " - CRM";
    } else if (missingCatchE) {
      return " - CE";
    }
    return "";
  };

  const comparisonRowRenderer = (
    row: Partial<Record<keyof TableDataDefinition, string>>
  ) => {
    const comparison =
      transactionComparison && row.transactionId
        ? transactionComparisonLookup[row.transactionId]
        : null;

    return {
      date: <Typography variant="bodyMedium">{row.date}</Typography>,
      transactionType: (
        <Typography variant="bodyMedium">{row.transactionType}</Typography>
      ),
      transactionStatus: (
        <Typography variant="bodyMedium">{row.transactionStatus}</Typography>
      ),
      comparisonStatus: (
        <Typography
          variant="bodyMedium"
          color={row.comparisonStatus !== "matching" ? "error.main" : undefined}
        >
          {row.comparisonStatus}
          {row.comparisonStatus === "missing"
            ? renderMissingText(!row.crmTransactionId, !row.ceTransactionId)
            : ""}
        </Typography>
      ),
      ceTransaction: (
        <Tooltip
          title={
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Box sx={{ display: "flex", columnGap: 1 }}>
                <Typography variant="bodyMedium" fontWeight={600}>
                  exGst:
                </Typography>
                <Typography variant="bodyMedium">
                  {comparison?.ceTransaction?.amountExGst
                    ? toDollars(comparison?.ceTransaction?.amountExGst, 2)
                    : ""}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", columnGap: 1 }}>
                <Typography variant="bodyMedium" fontWeight={600}>
                  incGst:
                </Typography>

                <Typography variant="bodyMedium">
                  {comparison?.ceTransaction?.amountIncGst
                    ? toDollars(comparison?.ceTransaction?.amountIncGst, 2)
                    : ""}
                </Typography>
              </Box>
            </Box>
          }
        >
          <Typography variant="bodyMedium">
            {comparison?.ceTransaction?.lastModifiedDate
              ? `${formatDate(
                  comparison.ceTransaction.lastModifiedDate,
                  DateTimeFormat
                )}`
              : ""}
          </Typography>
        </Tooltip>
      ),
      crmTransaction: (
        <Box sx={{ display: "flex", alignItems: "center", columnGap: 0.5 }}>
          {crmClientURL && comparison?.crmTransaction?.id && (
            <CRMButton
              url={getDossierTransactionCRMUrl(
                crmClientURL,
                comparison?.crmTransaction?.id
              )}
              sx={{ p: { sm: 1, xs: 0.5 } }}
              size="small"
            />
          )}
          <Tooltip
            title={
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Box sx={{ display: "flex", columnGap: 1 }}>
                  <Typography variant="bodyMedium" fontWeight={600}>
                    exGst:
                  </Typography>
                  <Typography variant="bodyMedium">
                    {comparison?.crmTransaction?.exGst
                      ? toDollars(comparison?.crmTransaction?.exGst, 2)
                      : ""}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", columnGap: 1 }}>
                  <Typography variant="bodyMedium" fontWeight={600}>
                    incGst:
                  </Typography>

                  <Typography variant="bodyMedium">
                    {comparison?.crmTransaction?.incGst
                      ? toDollars(comparison?.crmTransaction?.incGst, 2)
                      : ""}
                  </Typography>
                </Box>
              </Box>
            }
          >
            <Typography variant="bodyMedium">
              {comparison?.crmTransaction?.lastSyncDate
                ? `${formatDate(
                    comparison.crmTransaction.lastSyncDate,
                    DateTimeFormat
                  )}`
                : ""}
            </Typography>
          </Tooltip>
        </Box>
      ),
      mismatchedFields: comparison ? (
        renderMismatchedFields(comparison.mismatchedFields)
      ) : (
        <></>
      ),
    };
  };

  const buildComparisonRows = (
    comparisons: TransactionComparison[]
  ): Partial<Record<string, string>>[] => {
    return comparisons.map((comparison) => {
      return {
        transactionId: comparison.transactionId,
        date: formatDate(getDateForTransactionComparison(comparison)),
        transactionType:
          getTransactionTypeForTransactionComparison(comparison) ?? "",
        transactionStatus:
          getTransactionStatusForTransactionComparison(comparison) ?? "",
        comparisonStatus: comparison.status as
          | "matching"
          | "mismatching"
          | "missing",
        ceTransactionId: comparison.ceTransaction?.transactionId ?? "",
        crmTransactionId: comparison.crmTransaction?.catcheTransactionId ?? "",
        mismatchedFields:
          Object.keys(comparison.mismatchedFields).length > 0 ? "Yes" : "No",
      };
    });
  };

  return (
    <APTable
      columns={[
        "date",
        "transactionId",
        "transactionType",
        "transactionStatus",
        "comparisonStatus",
        "crmTransaction",
        "ceTransaction",
        "mismatchedFields",
      ]}
      headerRows={{
        date: { title: "Date" },
        transactionId: { title: "Transaction ID" },
        transactionType: { title: "Transaction Type" },
        transactionStatus: { title: "Transaction Status" },
        comparisonStatus: { title: "Comparison Status" },
        ceTransaction: { title: "CE Last Modified" },
        crmTransaction: { title: "CRM Last Synced" },
        mismatchedFields: { title: "Mismatched Fields" },
      }}
      rows={buildComparisonRows(transactionComparison)}
      rowRenderer={comparisonRowRenderer}
    />
  );
};

export default APInvoiceComparisonTable;
